import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { formatCurrency, formatPercentage } from "@/utils/formatters";
import { TargetCostData } from "@/types/target-cost";
import { calculateTargetCostMetrics } from "@/utils/target-cost-calculations";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MetricsGridProps {
  data: TargetCostData;
}

export function MetricsGrid({ data }: MetricsGridProps) {
  const metrics = calculateTargetCostMetrics(data);

  const tooltips = {
    painGainAmount: `The difference between the Target Cost and the Final PWDD (Price for Work Done to Date). Calculated as:
Pain/Gain Amount = Final PWDD - Target Cost.
A positive value indicates savings (gain), and a negative value indicates overruns (pain).`,
    
    contractorShare: `The contractor's portion of the total Pain/Gain amount, based on the cost-sharing percentages defined for each range. Calculated as:
Contractor Share = Sum of (Amount in Range × Share Percentage).
This value reflects how much of the cost variance the contractor bears.`,
    
    contractorMargin: `The contractor's total margin as a percentage of the Final PWDD. Calculated as:
Contractor Margin = (Total Contractor Share / Final PWDD) × 100.
This shows the contractor's profitability, factoring in the cost variance distribution.`,
    
    costVariance: `The percentage difference between the Target Cost and the Final PWDD. Calculated as:
Cost Variance = ((Final PWDD - Target Cost) / Target Cost) × 100.
A positive value indicates a percentage overrun, and a negative value indicates a percentage saving.`,
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <TooltipProvider>
        {/* Pain/Gain Amount */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="cursor-help">
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">Pain/Gain Amount</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-2xl font-semibold">
                    {formatCurrency(metrics.painGainAmount)}
                  </div>
                  {metrics.painGainAmount !== 0 && (
                    metrics.painGainAmount > 0 ? 
                      <TrendingUp className="text-green-500 h-4 w-4" /> : 
                      <TrendingDown className="text-destructive h-4 w-4" />
                  )}
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm p-4 text-sm">
            <p className="whitespace-pre-line">{tooltips.painGainAmount}</p>
          </TooltipContent>
        </Tooltip>

        {/* Contractor Share */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="cursor-help">
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">Contractor Share</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-2xl font-semibold">
                    {formatCurrency(metrics.totalContractorShare)}
                  </div>
                  {metrics.totalContractorShare !== 0 && (
                    metrics.totalContractorShare > 0 ? 
                      <TrendingUp className="text-green-500 h-4 w-4" /> : 
                      <TrendingDown className="text-destructive h-4 w-4" />
                  )}
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm p-4 text-sm">
            <p className="whitespace-pre-line">{tooltips.contractorShare}</p>
          </TooltipContent>
        </Tooltip>

        {/* Contractor Margin */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="cursor-help">
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">Contractor Margin</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-2xl font-semibold">
                    {formatPercentage(metrics.contractorMargin)}
                  </div>
                  {metrics.contractorMargin !== 0 && (
                    metrics.contractorMargin > 0 ? 
                      <TrendingUp className="text-green-500 h-4 w-4" /> : 
                      <TrendingDown className="text-destructive h-4 w-4" />
                  )}
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm p-4 text-sm">
            <p className="whitespace-pre-line">{tooltips.contractorMargin}</p>
          </TooltipContent>
        </Tooltip>

        {/* Cost Variance */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="cursor-help">
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">Cost Variance</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-2xl font-semibold">
                    {formatPercentage(metrics.costVariance)}
                  </div>
                  {metrics.costVariance !== 0 && (
                    metrics.costVariance < 0 ? 
                      <TrendingUp className="text-green-500 h-4 w-4" /> : 
                      <TrendingDown className="text-destructive h-4 w-4" />
                  )}
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm p-4 text-sm">
            <p className="whitespace-pre-line">{tooltips.costVariance}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}